import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Save, Trash2, AlertCircle, EyeOff, CheckCheck } from 'lucide-react';

export const isRowMissingPl = (r) => {
  const pl = (r.pl || '').trim();
  const en = (r.en || '').trim();
  return !pl || (pl === en && /[a-zA-Z]/.test(en) && !r.reviewed);
};

const isSameAsEn = (r) => (r.pl || '').trim() && (r.pl || '').trim() === (r.en || '').trim();

const LangCell = ({ row, lang, value, dirty, saving, onChange, onSave }) => (
  <td className="px-3 py-2 align-top">
    <div className="flex gap-1">
      <Input
        value={value}
        onChange={(e) => onChange(row.key, lang, e.target.value)}
        className={`text-sm ${dirty ? 'border-amber-500' : ''} ${row[`${lang}_override`] != null ? 'bg-emerald-50' : ''}`}
        data-testid={`translation-${lang}-${row.key}`}
      />
      {dirty && (
        <Button size="sm" onClick={() => onSave(row, lang)} disabled={saving} data-testid={`translation-save-${lang}-${row.key}`}>
          <Save className="h-3 w-3" />
        </Button>
      )}
    </div>
  </td>
);

const ResetButton = ({ row, lang, saving, onReset, title }) => (
  <Button size="sm" variant="ghost" onClick={() => onReset(row, lang)} disabled={saving} title={title}
    data-testid={`translation-reset-${lang}-${row.key}`}>
    <Trash2 className="h-3 w-3 text-slate-500" />
    <span className="ml-1 text-[10px] uppercase">{lang}</span>
  </Button>
);

export const TranslationRow = ({ row, draft, saving, onChange, onSave, onReset, onDismiss, onReviewed }) => {
  const { t } = useTranslation();
  const val = (lang) => draft?.[lang] ?? row[lang] ?? '';
  const dirty = (lang) => draft?.[lang] !== undefined && draft[lang] !== row[lang];
  return (
    <tr className="border-b hover:bg-slate-50/50" data-testid={`translation-row-${row.key}`}>
      <td className="px-3 py-2 font-mono text-xs text-slate-700 align-top break-all">
        <div className="flex items-start gap-1">
          <span>{row.key}</span>
          {isRowMissingPl(row) && <AlertCircle className="h-3 w-3 text-amber-500 flex-none mt-0.5" title={t('translationsEditor.missingHint')} />}
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          {(row.en_override != null || row.pl_override != null) && <span className="text-[10px] text-emerald-600">{t('translationsEditor.edited')}</span>}
          {row.group === 'email' && <Badge variant="outline" className="text-[10px] py-0">{t('translationsEditor.groupBadge.email')}</Badge>}
          {row.reviewed && isSameAsEn(row) && (
            <Badge variant="outline" className="text-[10px] py-0 cursor-pointer border-emerald-300 text-emerald-700"
              title={t('translationsEditor.unreview')} onClick={() => onReviewed(row, false)}
              data-testid={`translation-reviewed-badge-${row.key}`}>
              {t('translationsEditor.reviewedBadge')}
            </Badge>
          )}
          {row.group === 'detected' && <Badge variant="outline" className="text-[10px] py-0 border-sky-300 text-sky-700">{t('translationsEditor.groupBadge.detected')}</Badge>}
        </div>
      </td>
      {['en', 'pl'].map((lang) => (
        <LangCell key={lang} row={row} lang={lang} value={val(lang)} dirty={dirty(lang)}
          saving={saving[`${row.key}-${lang}`]} onChange={onChange} onSave={onSave} />
      ))}
      <td className="px-3 py-2 align-top text-right">
        <div className="flex justify-end gap-1">
          {row.en_override != null && <ResetButton row={row} lang="en" saving={saving[`${row.key}-en`]} onReset={onReset} title={t('translationsEditor.revertEn')} />}
          {row.pl_override != null && <ResetButton row={row} lang="pl" saving={saving[`${row.key}-pl`]} onReset={onReset} title={t('translationsEditor.revertPl')} />}
          {!row.reviewed && isSameAsEn(row) && (
            <Button size="sm" variant="ghost" onClick={() => onReviewed(row, true)} title={t('translationsEditor.markReviewed')}
              data-testid={`translation-mark-reviewed-${row.key}`}>
              <CheckCheck className="h-3 w-3 text-emerald-600" />
            </Button>
          )}
          {row.group === 'detected' && onDismiss && (
            <Button size="sm" variant="ghost" onClick={() => onDismiss(row)} title={t('translationsEditor.dismiss')}
              data-testid={`translation-dismiss-${row.key}`}>
              <EyeOff className="h-3 w-3 text-slate-500" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};
